import { httpService } from '@/utils/httpService';
import {
  AttachmentDownloadResponse,
  TipCreate,
  TipResponse,
  TipsQueryParams,
  TipUpdate,
} from '@/types/tips';

export class TipsService {
  // Basic CRUD operations
  async getTips(params: TipsQueryParams = {}): Promise<TipResponse[]> {
    return httpService.get<TipResponse[]>('/tips/', { params });
  }

  async getTip(tipId: string): Promise<TipResponse> {
    return httpService.get<TipResponse>(`/tips/${tipId}`);
  }

  async createTip(tip: TipCreate): Promise<TipResponse> {
    return httpService.post<TipResponse>('/tips/', tip);
  }

  async updateTip(tipId: string, update: TipUpdate): Promise<TipResponse> {
    return httpService.put<TipResponse>(`/tips/${tipId}`, update);
  }

  async deleteTip(tipId: string): Promise<{ message: string }> {
    return httpService.delete<{ message: string }>(`/tips/${tipId}`);
  }

  // Specialized queries
  async searchTips(
    query: string,
    params: { skip?: number; limit?: number } = {}
  ): Promise<TipResponse[]> {
    return httpService.get<TipResponse[]>('/tips/search', {
      params: { q: query, ...params },
    });
  }

  async getPendingTips(
    params: { skip?: number; limit?: number } = {}
  ): Promise<TipResponse[]> {
    return httpService.get<TipResponse[]>('/tips/pending', { params });
  }

  async getApprovedTips(
    params: { skip?: number; limit?: number } = {}
  ): Promise<TipResponse[]> {
    return httpService.get<TipResponse[]>('/tips/approved', { params });
  }

  async getHighPriorityTips(
    params: { skip?: number; limit?: number } = {}
  ): Promise<TipResponse[]> {
    return httpService.get<TipResponse[]>('/tips/high-priority', { params });
  }

  async getTipsBySource(
    source: string,
    params: { skip?: number; limit?: number } = {}
  ): Promise<TipResponse[]> {
    return httpService.get<TipResponse[]>(`/tips/source/${source}`, { params });
  }

  async getRecentTips(
    params: { skip?: number; limit?: number; days?: number } = {}
  ): Promise<TipResponse[]> {
    return httpService.get<TipResponse[]>('/tips/recent', { params });
  }

  // Status management
  async approveTip(tipId: string): Promise<TipResponse> {
    return httpService.post<TipResponse>(`/tips/${tipId}/approve`);
  }

  async rejectTip(tipId: string, reason?: string): Promise<TipResponse> {
    const data = reason ? { rejection_reason: reason } : undefined;
    return httpService.post<TipResponse>(`/tips/${tipId}/reject`, data);
  }

  async markTipInProgress(tipId: string): Promise<TipResponse> {
    return httpService.patch<TipResponse>(`/tips/${tipId}/in-progress`);
  }

  async markTipCompleted(tipId: string): Promise<TipResponse> {
    return httpService.patch<TipResponse>(`/tips/${tipId}/completed`);
  }

  // Content analysis
  async analyzeTipContent(tipId: string): Promise<any> {
    return httpService.post<any>(`/tips/${tipId}/analyze`);
  }

  async getSuggestedSources(tipId: string): Promise<any[]> {
    return httpService.get<any[]>(`/tips/${tipId}/suggested-sources`);
  }

  async getSimilarTips(
    tipId: string,
    params: { limit?: number } = {}
  ): Promise<TipResponse[]> {
    return httpService.get<TipResponse[]>(`/tips/${tipId}/similar`, { params });
  }

  // Bulk operations
  async bulkUpdateStatus(
    tipIds: string[],
    status: string
  ): Promise<{ updated: number; errors: any[] }> {
    return httpService.patch<{ updated: number; errors: any[] }>(
      '/tips/bulk-status',
      { tip_ids: tipIds, status }
    );
  }

  async bulkDelete(
    tipIds: string[]
  ): Promise<{ deleted: number; errors: any[] }> {
    return httpService.delete<{ deleted: number; errors: any[] }>(
      '/tips/bulk-delete',
      { data: { tip_ids: tipIds } }
    );
  }

  // IMAP ingestor
  async triggerImapIngest(): Promise<{ message: string; processed: number }> {
    return httpService.post<{ message: string; processed: number }>(
      '/ingestors/imap/ingest'
    );
  }

  // Attachment download
  async getAttachmentDownloadUrl(
    tipId: string,
    s3Key: string
  ): Promise<AttachmentDownloadResponse> {
    return httpService.post<AttachmentDownloadResponse>(
      `/tips/${tipId}/attachments/url`,
      { path: s3Key }
    );
  }
}

export const tipsService = new TipsService();
